'use client';

import Image from 'next/image';
import Link from 'next/link';
import { FC, useEffect, useRef, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import FadeIn from '@/components/FadeIn/FadeIn';
import { BOOK_DEMO_HREF } from '@/constants/publicConversionPaths';
import { getImageUrl } from '@/lib/cdn';
import './direct-agents.css';

/** Each prompt is a job the product does today, paired with the real screen that answers it. */
const PROMPTS = [
  {
    label: 'Build a list',
    text: 'Find manufacturers hiring Aruba or Cisco wireless engineers',
    image: getImageUrl('home/modal/enrichment-discover-2-v2.png'),
    imageAlt: 'MarketBetter found 22 people matching the search, listed by name and title',
  },
  {
    label: 'Identify',
    text: 'Which companies visited our pricing page this week?',
    image: getImageUrl('home/modal/visitor-id-modal-v2.png'),
    imageAlt: 'Companies visiting the website, ranked by score',
  },
  {
    label: 'Research',
    text: 'Add a column: what CRM does each account use?',
    image: getImageUrl('home/modal/enrichment-discover-1-v2.png'),
    imageAlt: 'Add a research column that fills in a value for every account',
  },
  {
    label: 'Prioritize',
    text: 'Who should my reps work first today?',
    image: getImageUrl('home/modal/daily-playbook-modal-v2.png'),
    imageAlt: 'Tasks ranked by buying signal, with the rep assigned to each',
  },
  {
    label: 'Reach out',
    text: 'Write a call opener and LinkedIn note for this VP of Operations',
    image: getImageUrl('home/modal/daily-playbook-discover-1-v2.png'),
    imageAlt: 'A personalized cold call script, with a LinkedIn tab beside it',
  },
] as const;

const AUTO_ADVANCE_MS = 5000;
const TYPE_MS = 22;

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

const DirectAgentsSection: FC = () => {
  const posthog = usePostHog();
  const [active, setActive] = useState(0);
  const [typed, setTyped] = useState<string>(PROMPTS[0].text);
  const [autoPlay, setAutoPlay] = useState(true);
  const [paused, setPaused] = useState(false);
  const reducedMotion = useRef(false);

  useEffect(() => {
    reducedMotion.current = prefersReducedMotion();
    if (reducedMotion.current) setAutoPlay(false);
  }, []);

  // Type the active prompt into the box.
  useEffect(() => {
    const text = PROMPTS[active].text;
    if (reducedMotion.current) {
      setTyped(text);
      return undefined;
    }
    setTyped('');
    let i = 0;
    const timer = window.setInterval(() => {
      i += 1;
      setTyped(text.slice(0, i));
      if (i >= text.length) window.clearInterval(timer);
    }, TYPE_MS);
    return () => window.clearInterval(timer);
  }, [active]);

  // Cycle through prompts until the visitor picks one.
  useEffect(() => {
    if (!autoPlay || paused) return undefined;
    const timer = window.setTimeout(
      () => setActive((i) => (i + 1) % PROMPTS.length),
      AUTO_ADVANCE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [active, autoPlay, paused]);

  const select = (index: number) => {
    setAutoPlay(false);
    setActive(index);
    posthog?.capture('homepage_direct_agents_prompt_clicked', { prompt: PROMPTS[index].label });
  };

  const current = PROMPTS[active];

  return (
    <section className='mx-auto max-w-[1240px]' aria-labelledby='direct-agents-heading'>
      <FadeIn variant='up' delay={0.1}>
        <div className='mx-auto max-w-[760px] text-center'>
          <h2
            id='direct-agents-heading'
            className='font-medium text-black'
            style={{
              fontSize: 'clamp(1.5rem, 2.5vw, 2.75rem)',
              lineHeight: 'clamp(2rem, 3.5vw, 3.5rem)',
            }}
          >
            Stop working lists. <span style={{ color: '#094FFE' }}>Start directing agents.</span>
          </h2>
          <p className='mt-4 text-base leading-7 text-slate-600 sm:text-lg sm:leading-8'>
            MarketBetter researches every account and drafts the outreach before your team opens it.
            Ask for a list, a question about your accounts, or a first message, in plain English.
          </p>
        </div>

        <div
          className='mt-10 grid gap-5 rounded-[20px] border border-[#EFEFEF] bg-white p-4 shadow-[0_8px_30px_rgba(2,16,55,0.06)] sm:p-6 lg:grid-cols-[340px_1fr]'
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div role='tablist' aria-label='Example prompts' className='flex flex-col gap-2'>
            {PROMPTS.map((prompt, index) => {
              const selected = index === active;
              return (
                <button
                  key={prompt.label}
                  type='button'
                  role='tab'
                  id={`agent-prompt-tab-${index}`}
                  aria-selected={selected}
                  aria-controls='agent-prompt-panel'
                  onClick={() => select(index)}
                  className={`relative overflow-hidden rounded-xl border px-4 py-3 text-left transition-colors ${
                    selected
                      ? 'border-[#A7BDFF] bg-[#F2F5FF]'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <span
                    className={`block text-xs font-medium uppercase tracking-wide ${
                      selected ? 'text-[#0848E7]' : 'text-slate-500'
                    }`}
                  >
                    {prompt.label}
                  </span>
                  <span className='mt-1 block text-sm leading-6 text-slate-800'>{prompt.text}</span>
                  {selected && autoPlay && !paused && (
                    <span
                      key={`progress-${active}`}
                      className='mb-agent-progress absolute bottom-0 left-0 h-0.5 bg-[#0848E7]'
                      style={{ animationDuration: `${AUTO_ADVANCE_MS}ms` }}
                      aria-hidden
                    />
                  )}
                </button>
              );
            })}
          </div>

          <div
            id='agent-prompt-panel'
            role='tabpanel'
            aria-labelledby={`agent-prompt-tab-${active}`}
            className='flex min-w-0 flex-col gap-4'
          >
            <div className='flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3'>
              <span className='h-2.5 w-2.5 shrink-0 rounded-full bg-[#3AE58A]' aria-hidden />
              <span className='min-h-6 flex-1 text-base text-slate-900'>
                {typed}
                <span
                  className='mb-agent-caret ml-0.5 inline-block h-5 w-px translate-y-1 bg-slate-900'
                  aria-hidden
                />
              </span>
            </div>
            <div
              className='relative overflow-hidden rounded-xl border border-slate-100'
              style={{
                aspectRatio: '16 / 9',
                background:
                  'radial-gradient(ellipse at 20% 80%, rgba(8, 72, 231, 0.08) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(58, 229, 138, 0.10) 0%, transparent 60%), #F7F9FF',
              }}
            >
              {PROMPTS.map((prompt, index) => (
                <Image
                  key={prompt.label}
                  src={prompt.image}
                  alt={index === active ? prompt.imageAlt : ''}
                  aria-hidden={index !== active}
                  fill
                  sizes='(max-width: 1024px) 100vw, 800px'
                  className={`object-contain p-3 transition-opacity duration-500 ${
                    index === active ? 'opacity-100' : 'opacity-0'
                  }`}
                  priority={index === 0}
                />
              ))}
            </div>
          </div>
        </div>

        <div className='mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row'>
          <Link
            href={BOOK_DEMO_HREF}
            className='inline-flex h-[50px] min-w-[190px] items-center justify-center rounded-[4px] bg-[#0848E7] px-[29px] text-base font-medium text-white transition-colors hover:bg-[#073dcc]'
            onClick={() =>
              posthog?.capture('homepage_direct_agents_cta_clicked', {
                cta: 'book_demo',
                prompt: current.label,
              })
            }
          >
            See it on your accounts
          </Link>
          <span className='text-sm text-slate-500'>
            Real screens from MarketBetter. Yours runs on your data.
          </span>
        </div>
      </FadeIn>
    </section>
  );
};

export default DirectAgentsSection;
